/**
 * Full-dataset CSV export (docs/06 §4).
 *
 * Every CMS list is server-paged, so serialising the rows TanStack currently holds exports the
 * visible page only — 50 of 80 grants, 25 of 1,738 review rows (QA 2026-09-14 CMS-22). The
 * screens hand `fetchAllRows` their own filtered query instead and it pages through the API
 * with `PageQuery`'s largest page (200) until it has `total` rows.
 */

/** `PageQuery.pageSize` maximum (`packages/shared/src/contracts/primitives.ts`). */
export const EXPORT_PAGE_SIZE = 200;

/** Ceiling for one download: this runs in the operator's browser, it is not a data dump. */
export const EXPORT_MAX_ROWS = 20_000;

export interface PagedRows<T> {
  items: T[];
  total: number;
}

export interface FetchAllOptions {
  pageSize?: number;
  maxRows?: number;
  /** Called after every page with the rows collected so far and the filtered total. */
  onProgress?: (fetched: number, total: number) => void;
}

export interface ExportResult<T> {
  rows: T[];
  /** Rows the filtered set holds, as the API reports it — not the number written. */
  total: number;
  /** `true` when `total` is larger than what `maxRows` allowed us to write (CMS-68). */
  truncated: boolean;
}

/**
 * Pages through `fetchPage` (1-based, like `PageQuery`) and returns every row of the filtered
 * set, capped at `maxRows`, **with the filtered total next to it**.
 *
 * The cap is deliberate — this runs in the operator's browser — but it used to be invisible:
 * /songs wrote 20,000 rows against a table reading 143,392 and said only "20000 rows exported",
 * so 123,392 songs went missing without a word (QA 2026-09-14 CMS-68). The caller gets `total`
 * and `truncated` so the button can say what was left out, and `onProgress` so the 5 minutes
 * the 100 round trips take are not a frozen screen.
 */
export async function fetchAllRowsResult<T>(
  fetchPage: (page: number, pageSize: number) => Promise<PagedRows<T>>,
  options: FetchAllOptions = {},
): Promise<ExportResult<T>> {
  const pageSize = Math.max(1, Math.min(options.pageSize ?? EXPORT_PAGE_SIZE, EXPORT_PAGE_SIZE));
  const maxRows = Math.max(1, options.maxRows ?? EXPORT_MAX_ROWS);
  const maxPages = Math.ceil(maxRows / pageSize);

  const rows: T[] = [];
  let total = 0;
  for (let page = 1; page <= maxPages; page++) {
    const result = await fetchPage(page, pageSize);
    total = result.total;
    rows.push(...result.items);
    options.onProgress?.(Math.min(rows.length, maxRows), total);
    if (result.items.length < pageSize) break;
    if (rows.length >= Math.min(result.total, maxRows)) break;
  }
  const capped = rows.length > maxRows ? rows.slice(0, maxRows) : rows;
  return { rows: capped, total: Math.max(total, capped.length), truncated: total > capped.length };
}

/** `fetchAllRowsResult` for callers that only want the rows. */
export async function fetchAllRows<T>(
  fetchPage: (page: number, pageSize: number) => Promise<PagedRows<T>>,
  options: FetchAllOptions = {},
): Promise<T[]> {
  return (await fetchAllRowsResult(fetchPage, options)).rows;
}
